import {
  forwardRef,
  Fragment,
  useRef,
  useState,
  useMemo,
  useEffect,
  useImperativeHandle
} from 'react';
import type { PropsWithoutRef, ReactNode, MouseEvent, FocusEvent } from 'react';

import {
  Text,
  SummaryItem,
  Avatar,
  Flex,
  EmailDisplay,
  Button,
  Icon,
  Sentiment,
  useOuterEvent,
  Popover,
  useElement,
  CardContent,
  useI18n,
  useBreakpoint,
  DateTimeDisplay,
  useConfiguration,
  MetaList,
  useScrollToggle,
  Status,
  Banner,
  useTheme,
  useEscape,
  HTML,
  Progress,
  getIntlDateTimeFormatFromCache,
  FileDisplayList,
  registerIcon,
  Count,
  useRefMap,
  getFocusables
} from '@pega/cosmos-react-core';
import type {
  ForwardRefForwardPropsComponent,
  FieldValueListProps,
  MetaListProps
} from '@pega/cosmos-react-core';
import { RichTextViewer } from '@pega/cosmos-react-rte';
import * as ArrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';
import * as ArrowMicroUpIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-up.icon';
import * as paperClipIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/paper-clip.icon';

import ContextMenuPopover from './ContextMenuPopover';
import type { EmailUser, EmailProps, TargetProps, ContextMenuProps } from './Email.types';
import {
  StyledEmailDisplay,
  StyledEmailHeader,
  StyledFromEmailDisplay,
  StyledEmailMoreInfoButton,
  StyledEmailMoreInfoPopover,
  StyledEmailPrimaryFieldValueList,
  StyledEmailBody,
  StyledEmailSecondaryFieldValueList,
  StyledSuggestedRepliesButton,
  StyledSuggestedRepliesMenuButton,
  StyledForwardedContentToggle,
  StyledEmailActions,
  StyledImportantText,
  StyledAttachmentButton
} from './Email.styles';
import highlightEntities from './utils/EntityHighlighter';
import './EmailEntity';

registerIcon(ArrowMicroUpIcon, ArrowMicroDownIcon, paperClipIcon);

const EmailDisplayList = ({
  emailUsers,
  showEmailAddress,
  showShortName
}: {
  emailUsers: EmailUser[];
  showEmailAddress?: boolean;
  showShortName?: boolean;
}) => {
  return (
    <>
      {emailUsers.map(({ emailAddress, fullName, shortName }, i) => (
        <Fragment key={emailAddress}>
          {i === 0 ? '' : '; '}
          <EmailDisplay
            value={emailAddress}
            displayText={`${showShortName ? shortName : fullName}${showEmailAddress ? ` <${emailAddress}>` : ''}`}
            variant='text'
            as={StyledEmailDisplay}
          />
        </Fragment>
      ))}
    </>
  );
};

const OVERFLOW_TO_EMAIL_COUNT = 2;
const OVERFLOW_EMAIL_SUGGESTION_COUNT = 2;

const Email: ForwardRefForwardPropsComponent<EmailProps> = forwardRef(function Email(
  props: PropsWithoutRef<EmailProps>,
  ref: EmailProps['ref']
) {
  const {
    id,
    from,
    to = [],
    cc = [],
    bcc = [],
    timeStamp,
    sentiment,
    subject,
    trail,
    attachments = [],
    suggestions = [],
    entityHighlightMapping = [],
    onReply,
    onForward,
    onReplyAll,
    onEditDraft,
    onDeleteDraft,
    onSuggestionClick,
    contextMenu,
    status,
    banner,
    body,
    markAsImportant = false,
    ...restProps
  } = props;
  const t = useI18n();
  const theme = useTheme();

  const [emailMoreInfoBtnRef, setEmailMoreInfoBtnRef] = useElement<HTMLButtonElement>(null);
  const [showEmailMoreInfo, setShowEmailMoreInfo] = useState(false);
  const [emailMoreInfoPopover, setEmailMoreInfoPopover] = useElement<HTMLDivElement>(null);
  const emailMoreInfoFields: FieldValueListProps['fields'] = [];
  // Only way to set this is through imperative handle
  const [contextMenuItems, setContextMenuItems] = useState<ContextMenuProps['items']>([]);
  const [contextMenuLoading, setContextMenuLoading] = useState<boolean>(false);

  const [contextMenuPopoverOpen, setContextMenuPopoverOpen] = useState(false);
  const { disableScroll, enableScroll } = useScrollToggle();
  const [contextMenuPopoverEl, setContextMenuPopoverEl] = useElement<HTMLElement>();
  const [tabRefMap, getTabRef] = useRefMap<HTMLDivElement | null>();

  useImperativeHandle(contextMenu?.handle, () => ({
    setItems: (ctxMenuItems: ContextMenuProps['items']) => {
      setContextMenuItems(ctxMenuItems || []);
    },
    setLoading(loading) {
      setContextMenuLoading(loading);
    },
    setOpen(visible) {
      setContextMenuPopoverOpen(visible);
    }
  }));

  const headerRef = useRef<HTMLDivElement>(null);
  const isSmallOrAbove = useBreakpoint('sm', {
    breakpointRef: headerRef,
    themeProp: 'content-width'
  });
  const isMediumOrAbove = useBreakpoint('md', {
    breakpointRef: headerRef,
    themeProp: 'content-width'
  });
  const { locale } = useConfiguration();
  const [currentTarget, setCurrentTarget] = useState<TargetProps | undefined>();

  useOuterEvent('mousedown', [contextMenuPopoverEl], () => {
    if (contextMenuPopoverOpen) setContextMenuPopoverOpen(false);
  });

  // Handler for right click on email body
  const onContextMenu = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.textContent?.trim()) {
      setCurrentTarget({
        targetNode: e.target,
        cursorPosition: {
          x: e.pageX - window.scrollX,
          y: e.pageY - window.scrollY
        }
      });
    }
    contextMenu?.onContextMenu(id, e);
  };

  const onItemClick: ContextMenuProps['onItemClick'] = (selectedValue: {
    fieldName: string;
    fieldValue: string;
  }) => {
    setContextMenuPopoverOpen(false);
    contextMenu?.onItemClick(selectedValue);
  };

  if (from) {
    emailMoreInfoFields.push({
      id: 'from',
      name: t('from'),
      value: from.emailAddress
    });
  }

  if (to.length > 0) {
    emailMoreInfoFields.push({
      id: 'to',
      name: t('to'),
      value: <EmailDisplayList emailUsers={to} showEmailAddress />
    });
  }

  if (cc.length > 0) {
    emailMoreInfoFields.push({
      id: 'cc',
      name: 'CC',
      value: <EmailDisplayList emailUsers={cc} showEmailAddress />
    });
  }

  if (bcc.length > 0) {
    emailMoreInfoFields.push({
      id: 'BCC',
      name: 'BCC',
      value: <EmailDisplayList emailUsers={bcc} showEmailAddress />
    });
  }

  if (timeStamp) {
    emailMoreInfoFields.push({
      id: 'date',
      name: t('date'),
      value: (
        <Text>
          {`${getIntlDateTimeFormatFromCache(locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(timeStamp))}, `}
          <DateTimeDisplay variant='datetime' value={timeStamp} />
        </Text>
      )
    });
  }

  useOuterEvent('mousedown', [emailMoreInfoPopover, emailMoreInfoBtnRef], () => {
    setShowEmailMoreInfo(false);
  });

  useEscape(() => {
    setShowEmailMoreInfo(false);
  });

  const secondaryFields: FieldValueListProps['fields'] = [];
  const attachmentsId = `${id}-attachments`;

  if (attachments.length > 0) {
    secondaryFields.push({
      id: 'attachments',
      name: '',
      value: <FileDisplayList items={attachments} ref={getTabRef(attachmentsId)} />
    });
  }

  if (suggestions.length > 0) {
    secondaryFields.push({
      id: 'suggested_replies',
      name: '',
      value: (
        <Flex container={{ wrap: 'wrap', gap: 0.5 }}>
          {suggestions
            .slice(0, OVERFLOW_EMAIL_SUGGESTION_COUNT)
            .map(({ id: suggestionId, title }) => (
              <StyledSuggestedRepliesButton
                variant='secondary'
                key={suggestionId}
                onClick={() => {
                  onSuggestionClick?.(id, suggestionId);
                }}
              >
                {title}
              </StyledSuggestedRepliesButton>
            ))}
          {suggestions.length > OVERFLOW_EMAIL_SUGGESTION_COUNT && (
            <StyledSuggestedRepliesMenuButton
              text={t('other_responses')}
              key='other_responses'
              variant='secondary'
              menu={{
                items: suggestions
                  .slice(OVERFLOW_EMAIL_SUGGESTION_COUNT)
                  .map(({ id: suggestionId, title }) => {
                    return {
                      primary: title,
                      id: suggestionId,
                      onClick: () => {
                        onSuggestionClick?.(id, suggestionId);
                      }
                    };
                  })
              }}
            />
          )}
        </Flex>
      )
    });
  }

  let renderedBody: ReactNode = body;

  renderedBody = useMemo(() => {
    // Apply entity highlighting only on string body
    if (typeof body === 'string') {
      let bodyString = body;

      if (subject) {
        bodyString = `<div style="font-weight: ${theme.base['font-weight']['semi-bold']}">${t(
          'subject_label',
          [subject]
        )}</div><br>${body}`;
      }

      if (entityHighlightMapping) {
        bodyString = highlightEntities(bodyString, entityHighlightMapping);
      }

      return (
        <>
          <HTML
            content={bodyString}
            customTags={['pega-email-entity']}
            aria-label={t('unique_entities', [entityHighlightMapping?.length ?? 0], {
              count: entityHighlightMapping?.length ?? 0
            })}
            role='group'
          />

          {contextMenu && currentTarget && (
            <ContextMenuPopover
              cursorPosition={currentTarget.cursorPosition}
              contextMenu={{
                ...contextMenu,
                items: contextMenuItems,
                loading: contextMenuLoading,
                onItemClick
              }}
              targetNode={currentTarget.targetNode}
              show={contextMenuPopoverOpen}
              ref={setContextMenuPopoverEl}
            />
          )}
        </>
      );
    }
  }, [
    currentTarget,
    contextMenu,
    contextMenuPopoverOpen,
    contextMenuItems,
    contextMenuLoading,
    entityHighlightMapping
  ]);

  const emailMetaListItems: MetaListProps['items'] = [
    markAsImportant && (
      <StyledImportantText variant='secondary'>{t('important')}</StyledImportantText>
    ),
    <Text variant='secondary'>
      {`${t('to')}: `}
      <EmailDisplayList emailUsers={to.slice(0, OVERFLOW_TO_EMAIL_COUNT)} showShortName />
      {to.length > OVERFLOW_TO_EMAIL_COUNT &&
        `; +${t('more_count', [to.length - OVERFLOW_TO_EMAIL_COUNT])}`}
    </Text>,
    <Text variant='secondary'>
      {`${getIntlDateTimeFormatFromCache(locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(timeStamp))}, `}
      <DateTimeDisplay variant='datetime' value={timeStamp} />
    </Text>
  ].filter(Boolean);

  useEffect(() => {
    if (contextMenuPopoverOpen) disableScroll();
    else enableScroll();
  }, [contextMenuPopoverOpen]);

  const actions = useMemo(() => {
    const applicableActions: ReactNode[] = [];

    if (attachments.length > 0) {
      applicableActions.push(
        <StyledAttachmentButton
          icon
          variant='simple'
          aria-label={t('attachments_count', [attachments.length])}
          label={t('article_attachments')}
          onClick={() => {
            const element = tabRefMap.get(attachmentsId);

            if (element) {
              const rect = element.getBoundingClientRect();
              const isInView =
                rect.top >= 0 && rect.bottom <= document.documentElement.clientHeight;

              if (!isInView) {
                element.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }

              getFocusables(element)[0]?.focus({ preventScroll: true });
            }
          }}
        >
          <Icon name='paper-clip' />
          <Count>{attachments.length}</Count>
        </StyledAttachmentButton>
      );
    }

    if (status !== 'draft') {
      if (onReply) {
        applicableActions.push(
          <Button
            variant='simple'
            icon
            label={t('reply')}
            onClick={() => {
              onReply?.(id);
            }}
          >
            <Icon name='reply' />
          </Button>
        );
      }
      if (onReplyAll) {
        applicableActions.push(
          <Button
            icon
            variant='simple'
            label={t('reply_all')}
            onClick={() => {
              onReplyAll?.(id);
            }}
          >
            <Icon name='reply-all' />
          </Button>
        );
      }
      if (onForward) {
        applicableActions.push(
          <Button
            variant='simple'
            icon
            label={t('forward')}
            onClick={() => {
              onForward?.(id);
            }}
          >
            <Icon name='forward' />
          </Button>
        );
      }
    }
    if (status === 'draft') {
      if (onEditDraft) {
        applicableActions.push(
          <Button
            variant='simple'
            icon
            label={t('edit')}
            onClick={() => {
              onEditDraft?.(id);
            }}
          >
            <Icon name='pencil' />
          </Button>
        );
      }
      if (onDeleteDraft) {
        applicableActions.push(
          <Button
            variant='simple'
            icon
            label={t('delete')}
            onClick={() => {
              onDeleteDraft?.(id);
            }}
          >
            <Icon name='trash' />
          </Button>
        );
      }
    }
    return applicableActions;
  }, [status, onEditDraft, onDeleteDraft, onReply, onReplyAll, onForward]);

  return (
    <Flex
      container={{
        direction: 'column',
        gap: 1
      }}
      ref={ref}
      {...restProps}
    >
      <SummaryItem
        as={StyledEmailHeader}
        visual={<Avatar {...from.avatarProps} name={from.fullName} />}
        ref={headerRef}
        primary={
          <Flex
            container={{
              gap: 1,
              alignItems: 'center'
            }}
          >
            <StyledFromEmailDisplay
              value={from.emailAddress}
              displayText={from.fullName}
              variant='text'
            />
            {sentiment && <Sentiment {...sentiment} labelHidden />}
            {status && isSmallOrAbove && (
              <Status variant={status === 'draft' ? 'pending' : 'urgent'}>
                {/* Passing mock count so that translation engine select correct form of draft,
                   with [](second argument) as empty so that count is not shown */}
                {status === 'draft' ? t('draft', [], { count: 1 }) : t(status)}
              </Status>
            )}
          </Flex>
        }
        secondary={
          <Flex
            container={{
              gap: 0,
              alignItems: 'start',
              direction: 'column'
            }}
          >
            <Flex
              container={{
                gap: 0,
                alignItems: 'start'
              }}
            >
              <MetaList
                items={
                  isMediumOrAbove
                    ? emailMetaListItems
                    : emailMetaListItems.slice(0, emailMetaListItems.length - 1)
                }
              />

              <Button
                icon
                variant='simple'
                as={StyledEmailMoreInfoButton}
                ref={setEmailMoreInfoBtnRef}
                onClick={() => {
                  setShowEmailMoreInfo(prev => !prev);
                }}
                onBlur={(e: FocusEvent<HTMLButtonElement>) => {
                  if (e.relatedTarget && !emailMoreInfoPopover?.contains(e.relatedTarget)) {
                    setShowEmailMoreInfo(false);
                  }
                }}
                label={showEmailMoreInfo ? t('show_less') : t('show_more')}
                aria-label={`${showEmailMoreInfo ? t('show_less') : t('show_more')} - ${t('mailing_details')}`}
              >
                <Icon name={showEmailMoreInfo ? 'arrow-micro-up' : 'arrow-micro-down'} />
              </Button>

              <Popover
                as={StyledEmailMoreInfoPopover}
                show={showEmailMoreInfo}
                ref={setEmailMoreInfoPopover}
                target={emailMoreInfoBtnRef}
                placement='bottom'
                portal={false}
              >
                <CardContent>
                  <StyledEmailPrimaryFieldValueList fields={emailMoreInfoFields} />
                </CardContent>
              </Popover>
            </Flex>
            {!isMediumOrAbove && timeStamp && emailMetaListItems[emailMetaListItems.length - 1]}
            {status && !isSmallOrAbove && (
              <Flex container={{ pad: [0.5, 0, 0] }}>
                <Status variant={status === 'draft' ? 'pending' : 'urgent'}>
                  {/* Passing mock count so that translation engine select correct form of draft,
                        with [](second argument) as empty so that count is not shown */}
                  {status === 'draft' ? t('draft', [], { count: 1 }) : t(status)}
                </Status>
              </Flex>
            )}
          </Flex>
        }
        actions={
          <Flex
            as={StyledEmailActions}
            container={{
              pad: 1,
              alignItems: 'center'
            }}
          >
            {...actions}
          </Flex>
        }
      />
      {banner && <Banner variant='urgent' {...banner} />}
      <Flex
        container={{
          direction: 'column',
          gap: 2,
          pad: [0, 2]
        }}
        onContextMenu={contextMenu ? onContextMenu : undefined}
      >
        {/* Attach the handler only when context menu enabled by providing the prop */}
        <StyledEmailBody>{renderedBody}</StyledEmailBody>
        {trail && (
          <>
            <StyledForwardedContentToggle
              variant='simple'
              icon
              onClick={() => {
                trail.onExpandCollapse(id);
              }}
              label={trail.expanded ? t('collapse_trail') : t('expand_trail')}
              compact
            >
              <Icon name='more-alt' />
            </StyledForwardedContentToggle>
            {trail.expanded && (
              <>
                {trail.content && trail.content.length > 0 && !trail.loading && (
                  <RichTextViewer content={trail?.content} type='html' />
                )}
                <Progress placement='block' visible={trail.loading} />
              </>
            )}
          </>
        )}
      </Flex>
      {secondaryFields.length > 0 && (
        <Flex
          container={{
            pad: [0, 2]
          }}
        >
          <StyledEmailSecondaryFieldValueList fields={secondaryFields} />
        </Flex>
      )}
    </Flex>
  );
});

export default Email;
